const CM_PER_METRE = 100;
const CM_PER_FOOT = 30.48;

function isAnchor(coordinate) {
  return Array.isArray(coordinate) && typeof coordinate[0] === 'number' && typeof coordinate[1] === 'number';
}

function isCurvedSegment(coordinate) {
  return Array.isArray(coordinate) && (coordinate[2] === 'curve' || coordinate[2] === 'bezier');
}

// Return every editable straight side in path order. A coordinate describes the
// segment that ends at that coordinate. Polygons also have a closing segment
// from the last anchor to the first; it is editable unless an explicit curved
// closing entry describes it.
export function straightPathSides(coordinates = [], closed = false) {
  const anchors = [];
  coordinates.forEach((coordinate, index) => {
    if (isAnchor(coordinate)) anchors.push({ coordinate, index });
  });

  const sides = [];
  for (let index = 1; index < anchors.length; index += 1) {
    const from = anchors[index - 1];
    const to = anchors[index];
    if (isCurvedSegment(to.coordinate)) continue;
    sides.push(makeSide(from, to, index));
  }

  if (closed && anchors.length > 1) {
    const closing = coordinates.find((coordinate) => Array.isArray(coordinate) && coordinate[0] === 'closing');
    const curvedClosing = closing && (closing[1] === 'curve' || closing[1] === 'bezier');
    if (!curvedClosing) {
      sides.push(makeSide(anchors[anchors.length - 1], anchors[0], anchors.length));
    }
  }

  return sides;
}

function makeSide(from, to, sideNumber) {
  return {
    fromIndex: from.index,
    toIndex: to.index,
    sideNumber,
    length: Math.hypot(to.coordinate[0] - from.coordinate[0], to.coordinate[1] - from.coordinate[1]),
  };
}

export function displaySideLength(planLength, units) {
  return planLength / (units === 'imperial' ? CM_PER_FOOT : CM_PER_METRE);
}

export function planSideLength(displayLength, units) {
  return displayLength * (units === 'imperial' ? CM_PER_FOOT : CM_PER_METRE);
}

// Mutate one side to an exact length. The destination anchor moves along the
// existing direction. Zero input intentionally collapses the side. If the side
// is already zero-length, a positive input uses the deterministic +x direction.
// Invalid and negative values are rejected without changing the coordinates.
export function setStraightSideLength(coordinates, side, desiredLength) {
  if (!side || !Number.isFinite(desiredLength) || desiredLength < 0) return false;
  const from = coordinates[side.fromIndex];
  const to = coordinates[side.toIndex];
  if (!isAnchor(from) || !isAnchor(to)) return false;

  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const currentLength = Math.hypot(dx, dy);

  if (desiredLength === 0) {
    to[0] = from[0];
    to[1] = from[1];
  } else if (currentLength === 0) {
    to[0] = from[0] + desiredLength;
    to[1] = from[1];
  } else {
    to[0] = from[0] + (dx / currentLength) * desiredLength;
    to[1] = from[1] + (dy / currentLength) * desiredLength;
  }
  return true;
}
